import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "panel-header draggable" }
const _hoisted_2 = { class: "panel-header__actions" }
const _hoisted_3 = {
  class: "drag-handle",
  title: "Drag to move"
}
const _hoisted_4 = { class: "channel-tabs" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "channel-tab__label" }
const _hoisted_7 = { class: "channel-tab__count" }
const _hoisted_8 = { class: "panel-body" }
const _hoisted_9 = { class: "modal-body-content" }
const _hoisted_10 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_11 = {
  key: 1,
  class: "error-container"
}
const _hoisted_12 = { class: "error-message" }
const _hoisted_13 = {
  key: 2,
  class: "interactions-list"
}
const _hoisted_14 = { class: "date-divider" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "interaction-content" }
const _hoisted_17 = { class: "interaction-header" }
const _hoisted_18 = { class: "interaction-title-group" }
const _hoisted_19 = { class: "interaction-title" }
const _hoisted_20 = {
  key: 0,
  class: "interaction-performer"
}
const _hoisted_21 = { class: "interaction-meta" }
const _hoisted_22 = { class: "interaction-date" }
const _hoisted_23 = { class: "interaction-time" }
const _hoisted_24 = { class: "social-card__header" }
const _hoisted_25 = { class: "social-card__label" }
const _hoisted_26 = {
  key: 0,
  class: "social-card__attachment"
}
const _hoisted_27 = {
  key: 1,
  class: "social-card__cta"
}
const _hoisted_28 = {
  key: 0,
  class: "social-card__preview"
}
const _hoisted_29 = {
  key: 1,
  class: "other-description"
}
const _hoisted_30 = {
  key: 3,
  class: "empty-container"
}
const _hoisted_31 = { class: "empty-icon-wrapper" }
const _hoisted_32 = { class: "empty-text" }
const _hoisted_33 = { class: "empty-title" }
const _hoisted_34 = { class: "empty-description" }
const _hoisted_35 = {
  key: 4,
  class: "pagination-wrapper"
}

import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useActivityLogs } from "@/composables/activityLogs/useActivityLogs";
import { TablePagination } from "@/components/ui";
import { TaskType } from "@/utils/enums";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import CallAnalysisAccordion from "@/components/call/CallAnalysisAccordion.vue";
import EmailCompositionModal from "@/components/bucket/email/EmailCompositionModal.vue";
import Unipile from "@/services/unipile/unipile";
import { getSocket } from "@/utils/socket";
import type { ActivityLogResponse } from "@/dtos/ActivityLog.dto";
import type { PaginationState } from "@/components/ui/table/types";

type ChannelTabId = "all" | TaskType;

interface Props {
  contactId: string;
  contactEmail?: string;
  initialChannel?: ChannelTabId;
}

interface ActivityLogGroup {
  label: string;
  items: ActivityLogResponse[];
}

interface ChannelTab {
  id: ChannelTabId;
  label: string;
  icon: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LastInteractionModal',
  props: {
    contactId: {},
    contactEmail: { default: "" },
    initialChannel: { default: "all" }
  },
  emits: ["closeLastInteractionModal", "navigateToTab"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const instance = getCurrentInstance();
const $filters = instance?.appContext.config.globalProperties.$filters;
const store = useStore();

const showModal = ref(true);
const expandedAccordionId = ref<string | null>(null);
const showFollowUpComposer = ref(false);
const followUpSubject = ref("");
const followUpBody = ref("");
const emailAccounts = ref<any[]>([]);

const account = computed(() => store.getters.account.data);

const currentPage = ref(1);
const pageSize = ref(10);

const selectedChannel = ref<ChannelTabId>(props.initialChannel);

// Drag state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};
  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [".panel-body", ".channel-tabs", "button", "a", "input", "textarea"];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

const activityLogsParams = computed(() => {
  if (!props.contactId || !account.value?.organizationId) return null;
  return {
    contactId: props.contactId,
    organizationId: account.value.organizationId,
    page: currentPage.value,
    limit: pageSize.value,
    taskType: selectedChannel.value === "all" ? undefined : selectedChannel.value,
  };
});

const { data: activityLogsData, isLoading, error, refetch } = useActivityLogs(activityLogsParams);

const errorMessage = computed(() => {
  if (!error.value) return "";
  if (error.value instanceof Error) {
    return error.value.message;
  }
  return String(error.value);
});

const activityLogs = computed<ActivityLogResponse[]>(() => {
  if (!activityLogsData.value) return [];
  return activityLogsData.value.logs;
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const FULL_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const getActivityLogDate = (log: ActivityLogResponse): Date | null => {
  const value = log.loggedOn ?? log.movedOn ?? log.createdAt;
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateGroupLabel = (date: Date): string => {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff > 1 && dayDiff < 7) return WEEKDAY_FORMATTER.format(date);
  return FULL_DATE_FORMATTER.format(date);
};

const groupedActivityLogs = computed<ActivityLogGroup[]>(() => {
  const groups: ActivityLogGroup[] = [];
  let current: ActivityLogGroup | null = null;

  for (const log of activityLogs.value) {
    const date = getActivityLogDate(log);
    const label = date ? getDateGroupLabel(date) : "Earlier";
    if (!current || current.label !== label) {
      current = { label, items: [] };
      groups.push(current);
    }
    current.items.push(log);
  }

  return groups;
});

const paginationData = computed<PaginationState | null>(() => {
  if (!activityLogsData.value) return null;

  const paginated = activityLogsData.value;
  return {
    page: paginated.pagination.page,
    perPage: paginated.pagination.limit,
    total: paginated.pagination.total,
  };
});

const countsByType = computed(() => {
  return (
    activityLogsData.value?.countsByType ?? {
      all: 0,
      call: 0,
      email: 0,
      linkedIn: 0,
      whatsApp: 0,
      sms: 0,
      other: 0,
    }
  );
});

const tabs: ChannelTab[] = [
  { id: "all", label: "All", icon: "mdi:view-list-outline" },
  { id: TaskType.call, label: "Calls", icon: "solar:phone-bold" },
  { id: TaskType.email, label: "Email", icon: "mdi:email-outline" },
  { id: TaskType.linkedIn, label: "LinkedIn", icon: "mdi:linkedin" },
  { id: TaskType.whatsApp, label: "WhatsApp", icon: "mdi:whatsapp" },
  { id: TaskType.sms, label: "SMS", icon: "mdi:message-text-outline" },
];

const tabCount = (id: ChannelTabId) => countsByType.value[id] ?? 0;

const handleTabClick = (id: ChannelTabId) => {
  if (selectedChannel.value === id) return;
  selectedChannel.value = id;
  currentPage.value = 1;
  expandedAccordionId.value = null;
};

const activeTabLabel = computed(
  () => tabs.find((t) => t.id === selectedChannel.value)?.label ?? "interaction"
);

const handlePageChange = (page: number) => {
  currentPage.value = page;
  const modalBody = document.querySelector(".modal-body-content");
  if (modalBody) {
    modalBody.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const handlePerPageChange = (perPage: number) => {
  pageSize.value = perPage;
  currentPage.value = 1;
  const modalBody = document.querySelector(".modal-body-content");
  if (modalBody) {
    modalBody.scrollTo({ top: 0, behavior: "smooth" });
  }
};

let socket: ReturnType<typeof getSocket> | null = null;

async function loadEmailAccounts() {
  try {
    const response = await Unipile.getConnectedChannels();
    const channels = response.data.data || [];
    emailAccounts.value = channels
      .filter((c: any) => c.type === "email")
      .map((account: any) => ({
        id: account.accountId,
        email: account.providerAccountId || account.name || "",
        name: account.providerAccountId || account.name || "",
        accountType: account.accountType,
        status: account.status,
        icon: account.icon,
      }));
  } catch (error) {
    console.error("Failed to load email accounts:", error);
  }
}

function handleSwitchChannel(payload: { channel: string; subject?: string; content?: string }) {
  if (payload.channel === "email") {
    followUpSubject.value = payload.subject || "";
    followUpBody.value = payload.content || "";
    showFollowUpComposer.value = true;
  }
}

function handleFollowUpSent() {
  showFollowUpComposer.value = false;
}

onMounted(() => {
  socket = getSocket();
  socket.on("call:analysis:complete", () => refetch());
  loadEmailAccounts();
});

onBeforeUnmount(() => {
  if (socket) {
    socket.off("call:analysis:complete");
  }
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});

const handleClose = () => {
  emit("closeLastInteractionModal");
};

const handleAccordionExpand = (id: string) => {
  expandedAccordionId.value = id;
};

const handleAccordionCollapse = () => {
  expandedAccordionId.value = null;
};

/** Maps a task type to the tab id used in TaskFollowupModal, or null for non-routable types. */
const getTabForTaskType = (taskType: string): string | null => {
  switch (taskType) {
    case TaskType.email:
      return "email";
    case TaskType.linkedIn:
      return "linkedin";
    case TaskType.whatsApp:
      return "whatsapp";
    case TaskType.sms:
      return "sms";
    default:
      return null;
  }
};

const resolveTaskType = (activityLog: ActivityLogResponse): string =>
  activityLog.taskType || TaskType.other;

/** The conversation/thread id to auto-open for this activity log, per channel. */
const getConversationIdForTaskType = (
  taskType: string,
  activityLog: ActivityLogResponse
): string | null => {
  switch (taskType) {
    case TaskType.email:
      return activityLog.metadata?.threadId || null;
    case TaskType.linkedIn:
    case TaskType.whatsApp:
      return activityLog.metadata?.conversationId || null;
    default:
      return null;
  }
};

/** The specific message to scroll to/highlight within the conversation, per channel. */
const getMessageIdForTaskType = (
  taskType: string,
  activityLog: ActivityLogResponse
): string | null => {
  switch (taskType) {
    case TaskType.linkedIn:
    case TaskType.whatsApp:
      return activityLog.metadata?.externalId || null;
    default:
      return null;
  }
};

const handleCardClick = (activityLog: ActivityLogResponse) => {
  const taskType = resolveTaskType(activityLog);
  const tab = getTabForTaskType(taskType);
  if (tab) {
    const conversationId = getConversationIdForTaskType(taskType, activityLog);
    const messageId = getMessageIdForTaskType(taskType, activityLog);
    emit("navigateToTab", tab, conversationId, messageId);
  }
  emit("closeLastInteractionModal");
};

const getPreviewContent = (activityLog: ActivityLogResponse): string | null => {
  const preview = activityLog.metadata?.preview || activityLog.metadata?.smsPreview;
  if (!preview) return null;
  return preview.length > 150 ? preview.substring(0, 150) + "..." : preview;
};

const getInteractionIcon = (taskType: string) => {
  switch (taskType) {
    case TaskType.call:
      return "mdi:phone-in-talk";
    case TaskType.email:
      return "mdi:email-outline";
    case TaskType.linkedIn:
      return "mdi:linkedin";
    case TaskType.whatsApp:
      return "mdi:whatsapp";
    case TaskType.sms:
      return "mdi:message-text-outline";
    case TaskType.other:
      return "mdi:dots-horizontal-circle-outline";
    default:
      return "mdi:dots-horizontal-circle-outline";
  }
};

const getIconBadgeClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.call:
      return "icon-badge--call";
    case TaskType.email:
      return "icon-badge--email";
    case TaskType.linkedIn:
      return "icon-badge--linkedin";
    case TaskType.whatsApp:
      return "icon-badge--whatsapp";
    case TaskType.sms:
      return "icon-badge--sms";
    case TaskType.other:
      return "icon-badge--other";
    default:
      return "icon-badge--other";
  }
};

const getInteractionTitle = (activityLog: ActivityLogResponse) => {
  const taskType = resolveTaskType(activityLog);
  const direction = activityLog.metadata?.direction;
  switch (taskType) {
    case TaskType.call:
      return direction === "inbound" ? "Incoming call" : "Call has been made";
    case TaskType.email:
      return direction === "inbound" ? "Email received from contact" : "Email sent to contact";
    case TaskType.linkedIn:
      return direction === "inbound" ? "LinkedIn message received" : "LinkedIn message sent";
    case TaskType.whatsApp:
      return direction === "inbound" ? "WhatsApp message received" : "WhatsApp message sent";
    case TaskType.sms:
      return direction === "inbound" ? "SMS received from contact" : "SMS sent to contact";
    case TaskType.other:
      return "Interaction logged";
    default:
      return "Interaction logged";
  }
};

const getSocialCardClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "social-card--email";
    case TaskType.linkedIn:
      return "social-card--linkedin";
    case TaskType.whatsApp:
      return "social-card--whatsapp";
    case TaskType.sms:
      return "social-card--sms";
    default:
      return "social-card--other";
  }
};

const getSocialCardLabel = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "EMAIL";
    case TaskType.linkedIn:
      return "LINKEDIN";
    case TaskType.whatsApp:
      return "WHATSAPP";
    case TaskType.sms:
      return "SMS";
    default:
      return "OTHER";
  }
};

const formattedDate = (activityLog: ActivityLogResponse) => {
  const dateValue = activityLog.loggedOn ?? activityLog.movedOn ?? activityLog.createdAt;
  if (!dateValue || !$filters) return "N/A";
  return $filters.getDateOnly(dateValue);
};

const formattedTime = (activityLog: ActivityLogResponse) => {
  const dateValue = activityLog.loggedOn ?? activityLog.movedOn ?? activityLog.createdAt;
  if (!dateValue || !$filters) return "N/A";
  return $filters.getTimeOnly(dateValue);
};

const getIconColorClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "icon-color--email";
    case TaskType.call:
      return "icon-color--call";
    case TaskType.linkedIn:
      return "icon-color--linkedin";
    case TaskType.whatsApp:
      return "icon-color--whatsapp";
    case TaskType.sms:
      return "icon-color--sms";
    case TaskType.other:
      return "icon-color--other";
    default:
      return "icon-color--other";
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_Transition, { name: "modal-fade" }, {
      default: _withCtx(() => [
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["draggable-overlay", { 'dragging-active': !isInitialPosition.value }])
            }, [
              _createElementVNode("div", {
                ref_key: "modalRef",
                ref: modalRef,
                class: _normalizeClass(["draggable-panel", { 'is-dragging': isDragging.value }]),
                style: _normalizeStyle(modalStyle.value),
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
                onMousedown: startDrag,
                onTouchstart: startDrag
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "panel-header__text" }, [
                    _createElementVNode("h6", { class: "panel-header__title" }, "Last Interaction History"),
                    _createElementVNode("p", { class: "panel-header__subtitle" }, "View past outreach activity")
                  ], -1)),
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:drag",
                        width: "16",
                        height: "16"
                      })
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "panel-header__close",
                      onClick: handleClose,
                      "aria-label": "Close"
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:close",
                        width: "18",
                        height: "18"
                      })
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tabs, (tab) => {
                    return _createElementVNode("button", {
                      key: tab.id,
                      type: "button",
                      class: _normalizeClass([
                'channel-tab',
                { active: tab.id === selectedChannel.value, dim: tabCount(tab.id) === 0 },
              ]),
                      onClick: ($event: any) => (handleTabClick(tab.id))
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: tab.icon,
                        width: "14",
                        height: "14"
                      }, null, 8, ["icon"]),
                      _createElementVNode("span", _hoisted_6, _toDisplayString(tab.label), 1),
                      _createElementVNode("span", _hoisted_7, _toDisplayString(tabCount(tab.id)), 1)
                    ], 10, _hoisted_5)
                  }), 64))
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    (_unref(isLoading))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createVNode(LoadingBars, { size: "sm" })
                        ]))
                      : (_unref(error))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:alert-circle-outline",
                              width: "24",
                              height: "24",
                              class: "error-icon"
                            }),
                            _createElementVNode("p", _hoisted_12, _toDisplayString(errorMessage.value), 1),
                            _createElementVNode("button", {
                              type: "button",
                              class: "btn btn-primary btn-sm",
                              onClick: _cache[0] || (_cache[0] = () => _unref(refetch)())
                            }, " Retry ")
                          ]))
                        : (activityLogs.value && activityLogs.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedActivityLogs.value, (group, groupIndex) => {
                                return (_openBlock(), _createElementBlock(_Fragment, {
                                  key: `group-${groupIndex}-${group.label}`
                                }, [
                                  _createElementVNode("div", _hoisted_14, _toDisplayString(group.label), 1),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (activityLog, itemIndex) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                      key: activityLog.id || `${groupIndex}-${itemIndex}`
                                    }, [
                                      (resolveTaskType(activityLog) === _unref(TaskType).call)
                                        ? (_openBlock(), _createBlock(CallAnalysisAccordion, {
                                            key: 0,
                                            "call-sid": activityLog.callSid || null,
                                            "activity-log": activityLog,
                                            "is-expanded": expandedAccordionId.value === activityLog.id,
                                            "recording-sid": activityLog.callLog?.recordingSid || null,
                                            "recording-url": activityLog.callLog?.recordingUrl || null,
                                            "call-status": activityLog.callLog?.status || null,
                                            "logged-date": formattedDate(activityLog),
                                            "logged-time": formattedTime(activityLog),
                                            "performed-by": activityLog.createdBy?.fullName || null,
                                            onExpand: ($event: any) => (handleAccordionExpand(activityLog.id)),
                                            onCollapse: handleAccordionCollapse,
                                            onSwitchChannel: handleSwitchChannel
                                          }, null, 8, ["call-sid", "activity-log", "is-expanded", "recording-sid", "recording-url", "call-status", "logged-date", "logged-time", "performed-by", "onExpand"]))
                                        : (_openBlock(), _createElementBlock("div", {
                                            key: 1,
                                            class: _normalizeClass(["interaction-card", {
                        'interaction-card--clickable': !!getTabForTaskType(
                          resolveTaskType(activityLog)
                        ),
                      }]),
                                            onClick: ($event: any) => (handleCardClick(activityLog))
                                          }, [
                                            _createElementVNode("div", {
                                              class: _normalizeClass(["interaction-icon-badge", getIconBadgeClass(resolveTaskType(activityLog))])
                                            }, [
                                              _createVNode(_unref(Icon), {
                                                icon: getInteractionIcon(resolveTaskType(activityLog)),
                                                width: "18",
                                                height: "18",
                                                class: _normalizeClass(getIconColorClass(resolveTaskType(activityLog)))
                                              }, null, 8, ["icon", "class"])
                                            ], 2),
                                            _createElementVNode("div", _hoisted_16, [
                                              _createElementVNode("div", _hoisted_17, [
                                                _createElementVNode("div", _hoisted_18, [
                                                  _createElementVNode("h6", _hoisted_19, _toDisplayString(getInteractionTitle(activityLog)), 1),
                                                  (activityLog.createdBy?.fullName)
                                                    ? (_openBlock(), _createElementBlock("span", _hoisted_20, [
                                                        _createVNode(_unref(Icon), {
                                                          icon: "mdi:account-outline",
                                                          width: "11",
                                                          height: "11"
                                                        }),
                                                        _createTextVNode(" " + _toDisplayString(activityLog.createdBy.fullName), 1)
                                                      ]))
                                                    : _createCommentVNode("", true)
                                                ]),
                                                _createElementVNode("div", _hoisted_21, [
                                                  _createElementVNode("span", _hoisted_22, _toDisplayString(formattedDate(activityLog)), 1),
                                                  _createElementVNode("span", _hoisted_23, _toDisplayString(formattedTime(activityLog)), 1)
                                                ])
                                              ]),
                                              (resolveTaskType(activityLog) !== _unref(TaskType).other)
                                                ? (_openBlock(), _createElementBlock("div", {
                                                    key: 0,
                                                    class: _normalizeClass(["social-card", getSocialCardClass(resolveTaskType(activityLog))])
                                                  }, [
                                                    _createElementVNode("div", _hoisted_24, [
                                                      _createVNode(_unref(Icon), {
                                                        icon: getInteractionIcon(resolveTaskType(activityLog)),
                                                        width: "14",
                                                        height: "14",
                                                        class: "social-card__icon"
                                                      }, null, 8, ["icon"]),
                                                      _createElementVNode("span", _hoisted_25, _toDisplayString(getSocialCardLabel(resolveTaskType(activityLog))), 1),
                                                      (activityLog.metadata?.hasAttachments)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_26, [
                                                            _createVNode(_unref(Icon), {
                                                              icon: "mdi:attachment",
                                                              width: "12",
                                                              height: "12"
                                                            })
                                                          ]))
                                                        : _createCommentVNode("", true),
                                                      (!!getTabForTaskType(resolveTaskType(activityLog)))
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_27, [
                                                            _cache[4] || (_cache[4] = _createTextVNode(" Open ", -1)),
                                                            _createVNode(_unref(Icon), {
                                                              icon: "mdi:arrow-right",
                                                              width: "12",
                                                              height: "12"
                                                            })
                                                          ]))
                                                        : _createCommentVNode("", true)
                                                    ]),
                                                    (getPreviewContent(activityLog))
                                                      ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(getPreviewContent(activityLog)), 1))
                                                      : _createCommentVNode("", true)
                                                  ], 2))
                                                : _createCommentVNode("", true),
                                              (
                            resolveTaskType(activityLog) === _unref(TaskType).other &&
                            (activityLog.note || activityLog.description)
                          )
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                                                    _createElementVNode("span", null, _toDisplayString(activityLog.note || activityLog.description), 1)
                                                  ]))
                                                : _createCommentVNode("", true)
                                            ])
                                          ], 10, _hoisted_15))
                                    ], 64))
                                  }), 128))
                                ], 64))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_30, [
                              _createElementVNode("div", _hoisted_31, [
                                _createVNode(_unref(Icon), {
                                  icon: "mdi:history",
                                  width: "28",
                                  height: "28",
                                  class: "empty-icon"
                                })
                              ]),
                              _createElementVNode("div", _hoisted_32, [
                                _createElementVNode("p", _hoisted_33, _toDisplayString(selectedChannel.value === "all"
                        ? "No interactions yet"
                        : `No ${activeTabLabel.value} interactions yet`), 1),
                                _createElementVNode("p", _hoisted_34, _toDisplayString(selectedChannel.value === "all"
                        ? "Calls, emails, and messages with this contact will appear here."
                        : "Try a different channel or check back later."), 1)
                              ])
                            ])),
                    (
                  paginationData.value && Math.ceil(paginationData.value.total / paginationData.value.perPage) > 1
                )
                      ? (_openBlock(), _createElementBlock("div", _hoisted_35, [
                          _createVNode(_unref(TablePagination), {
                            pagination: paginationData.value,
                            "per-page-options": [10, 25, 50],
                            onPageChange: handlePageChange,
                            onPerPageChange: handlePerPageChange
                          }, null, 8, ["pagination"])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ], 38)
            ], 2))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(EmailCompositionModal, {
      show: showFollowUpComposer.value,
      "recipient-email": props.contactEmail,
      accounts: emailAccounts.value,
      "initial-subject": followUpSubject.value,
      "initial-body": followUpBody.value,
      "contact-id": props.contactId,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (showFollowUpComposer.value = false)),
      onSend: handleFollowUpSent
    }, null, 8, ["show", "recipient-email", "accounts", "initial-subject", "initial-body", "contact-id"])
  ]))
}
}

})